import { useEffect, useId, useRef, useState } from 'react'
import type { CSSProperties, DragEvent } from 'react'
import { AlertTriangle, Check, Link, Trash2, Upload, X } from 'lucide-react'
import { uploadMediaImage } from '@shared/api/media'
import type { MediaImageKind, MediaUploadResponse } from '@shared/api/media'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { FieldFeedback } from '../Field/FieldFeedback'
import { TextField } from '../Input/TextField'
import {
  actionBar,
  chip,
  chips,
  dropzone,
  emptyBody,
  errorBox,
  fileMeta,
  fieldHeader,
  footer,
  helper as helperClass,
  icon,
  iconButton,
  input,
  label as labelClass,
  linkButton,
  preview,
  previewFallback,
  progressFill,
  progressShell,
  root,
  status,
  title,
  uploadPanel,
  urlPanel,
} from './ImageUploadField.css'

const ACCEPTED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp'])
const MAX_IMAGE_BYTES = 5 * 1024 * 1024

export interface ImageUploadFieldProps {
  readonly label: string
  readonly kind: MediaImageKind
  readonly value: string
  readonly onChange: (url: string) => void
  readonly aspect?: string
  readonly hint?: string
  readonly maxInlineSize?: number
  readonly allowUrl?: boolean
  readonly error?: string
}

type UploadState = 'idle' | 'dragging' | 'uploading' | 'ready' | 'error'

export function ImageUploadField({
  label,
  kind,
  value,
  onChange,
  aspect = '1 / 1',
  hint,
  maxInlineSize,
  allowUrl = false,
  error,
}: ImageUploadFieldProps) {
  const inputId = useId()
  const [state, setState] = useState<UploadState>('idle')
  const [localPreview, setLocalPreview] = useState<string>('')
  const [progress, setProgress] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploaded, setUploaded] = useState<MediaUploadResponse | null>(null)
  const [fileName, setFileName] = useState('')
  const [showUrl, setShowUrl] = useState(false)
  const [failedPreviewUrl, setFailedPreviewUrl] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const localPreviewRef = useRef('')
  const feedbackId = `${inputId}-feedback`
  const displayUrl = state === 'uploading' ? localPreview || value : value
  const hasPreview = displayUrl.length > 0
  const previewFailed = failedPreviewUrl === displayUrl
  const message = uploadError ?? error
  const fieldFeedback = state === 'error' && uploadError ? null : message
  const visualState: UploadState =
    state === 'uploading' || state === 'dragging' || state === 'error'
      ? state
      : hasPreview
        ? 'ready'
        : 'idle'
  const showImagePreview = hasPreview && visualState !== 'error' && visualState !== 'dragging'
  const dropzoneState: UploadState = showImagePreview ? 'ready' : visualState

  useEffect(
    () => () => {
      abortRef.current?.abort()
      if (localPreviewRef.current) {
        URL.revokeObjectURL(localPreviewRef.current)
        localPreviewRef.current = ''
      }
    },
    [],
  )

  useEffect(() => {
    if (state === 'uploading' || !localPreviewRef.current) return
    URL.revokeObjectURL(localPreviewRef.current)
    localPreviewRef.current = ''
    setLocalPreview('')
  }, [value, state])

  function setLocalPreviewUrl(nextValue: string) {
    localPreviewRef.current = nextValue
    setLocalPreview(nextValue)
  }

  function revokeLocalPreviewUrl() {
    if (localPreviewRef.current) {
      URL.revokeObjectURL(localPreviewRef.current)
    }
    setLocalPreviewUrl('')
  }

  function clearLocalPreview() {
    revokeLocalPreviewUrl()
    setUploaded(null)
    setFileName('')
    setProgress(0)
    setFailedPreviewUrl(null)
  }

  function validateFile(file: File): string | null {
    if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
      return 'Formato nao suportado. Use PNG, JPG ou WEBP.'
    }
    if (file.size > MAX_IMAGE_BYTES) {
      return 'Imagem muito grande. O limite e 5 MB.'
    }
    return null
  }

  async function handleFile(file: File) {
    const validationError = validateFile(file)
    if (validationError) {
      setUploadError(validationError)
      setState('error')
      return
    }

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setFileName(file.name)
    setUploadError(null)
    setProgress(0)
    setUploaded(null)
    setFailedPreviewUrl(null)
    setState('uploading')
    revokeLocalPreviewUrl()
    const previewUrl = URL.createObjectURL(file)
    setLocalPreviewUrl(previewUrl)

    try {
      const response = await uploadMediaImage(file, kind, {
        signal: controller.signal,
        onProgress: setProgress,
      })
      setProgress(100)
      setUploaded(response)
      setState('idle')
      revokeLocalPreviewUrl()
      onChange(response.url)
    } catch (cause) {
      if (controller.signal.aborted) {
        clearLocalPreview()
        setState('idle')
        return
      }
      setUploadError(
        getUserFacingError(cause, {
          title: 'Não foi possível enviar a imagem',
          fallback: 'Escolha outro arquivo ou tente novamente.',
        }).description,
      )
      setState('error')
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null
      }
    }
  }

  function onDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    const file = event.dataTransfer.files.item(0)
    if (file) {
      void handleFile(file)
    }
  }

  function resetImage() {
    abortRef.current?.abort()
    clearLocalPreview()
    setUploadError(null)
    setState('idle')
    onChange('')
  }

  const dropzoneStyle = {
    '--image-upload-aspect': aspect,
    ...(maxInlineSize ? { '--image-upload-max-inline-size': `${maxInlineSize}px` } : null),
  } as CSSProperties

  return (
    <div className={root} style={dropzoneStyle}>
      <div className={fieldHeader}>
        <span className={labelClass}>{label}</span>
        <div className={actionBar}>
          {visualState === 'uploading' ? (
            <button
              type="button"
              className={iconButton}
              aria-label="Cancelar envio"
              onClick={() => abortRef.current?.abort()}
            >
              <X size={15} />
            </button>
          ) : hasPreview ? (
            <button
              type="button"
              className={iconButton}
              aria-label="Remover imagem"
              onClick={resetImage}
            >
              <Trash2 size={15} />
            </button>
          ) : allowUrl ? (
            <button
              type="button"
              className={linkButton}
              onClick={() => setShowUrl((current) => !current)}
            >
              <Link size={13} /> {showUrl ? 'Ocultar URL' : 'Colar URL'}
            </button>
          ) : null}
        </div>
      </div>
      <label
        className={dropzone({ state: dropzoneState })}
        htmlFor={inputId}
        onDragOver={(event) => event.preventDefault()}
        onDragEnter={(event) => {
          event.preventDefault()
          setState('dragging')
        }}
        onDragLeave={(event) => {
          event.preventDefault()
          setState('idle')
        }}
        onDrop={onDrop}
      >
        {showImagePreview ? (
          <>
            {previewFailed ? (
              <span className={previewFallback} aria-hidden="true">
                <Upload size={22} />
              </span>
            ) : (
              <img
                className={preview}
                src={displayUrl}
                alt=""
                onError={() => setFailedPreviewUrl(displayUrl)}
              />
            )}
            <span className={footer}>
              {visualState === 'uploading' ? (
                <>
                  <span className={status}>Enviando... {progress}%</span>
                  <span className={fileMeta}>{fileName || 'Imagem'}</span>
                </>
              ) : (
                <>
                  <span className={status}>
                    <Check size={14} />
                    Enviada
                  </span>
                  <span className={fileMeta}>{mediaMeta(fileName, uploaded)}</span>
                </>
              )}
            </span>
          </>
        ) : visualState === 'uploading' ? (
          <span className={uploadPanel}>
            <span className={title}>{fileName || 'Imagem'}</span>
            <span className={progressShell}>
              <span className={progressFill} style={{ width: `${Math.max(progress, 8)}%` }} />
            </span>
            <span className={helperClass}>Enviando... {progress}%</span>
          </span>
        ) : visualState === 'dragging' ? (
          <span className={emptyBody}>
            <span className={icon}>
              <Upload size={20} />
            </span>
            <span className={title}>Solte para enviar</span>
            <span className={helperClass}>Pode soltar; a gente cuida do resto.</span>
          </span>
        ) : visualState === 'error' ? (
          <span className={errorBox} role="alert">
            <span className={icon}>
              <AlertTriangle size={20} />
            </span>
            <span className={title}>Deu erro</span>
            <span className={helperClass}>{message}</span>
          </span>
        ) : (
          <span className={emptyBody}>
            <span className={icon}>
              <Upload size={20} />
            </span>
            <span className={title}>Arraste uma imagem ou clique para enviar</span>
            <span className={helperClass}>Solte o arquivo aqui</span>
            <span className={chips}>
              <span className={chip}>PNG, JPG ou WEBP</span>
              <span className={chip}>até 5 MB</span>
              {hint ? <span className={chip}>{hint}</span> : null}
            </span>
          </span>
        )}
        <input
          id={inputId}
          className={input}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          aria-label={label}
          aria-invalid={Boolean(message)}
          aria-describedby={fieldFeedback ? feedbackId : undefined}
          onChange={(event) => {
            const file = event.target.files?.[0]
            event.target.value = ''
            if (file) {
              void handleFile(file)
            }
          }}
        />
      </label>

      {allowUrl && showUrl ? (
        <div className={urlPanel}>
          <TextField
            label="URL da imagem"
            value={value}
            placeholder="https://..."
            error={error}
            onChange={(event) => {
              setUploadError(null)
              onChange(event.target.value)
            }}
          />
        </div>
      ) : null}
      <FieldFeedback id={feedbackId} status={fieldFeedback ? 'error' : undefined}>
        {fieldFeedback}
      </FieldFeedback>
    </div>
  )
}

function mediaMeta(fileName: string, uploaded: MediaUploadResponse | null): string {
  const name = uploaded?.objectKey.split('/').pop() ?? fileName
  if (!uploaded) {
    return name || 'imagem'
  }
  return `${name} · ${uploaded.width}x${uploaded.height} · ${formatBytes(uploaded.sizeBytes)}`
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 102.4) / 10} KB`
  }
  return `${Math.round(bytes / 1024 / 102.4) / 10} MB`
}
